import { useState, useEffect, useCallback, useRef } from 'react';
import { renderMarkdown, getAllThemes } from '@markdawn/core';
import type { RenderResult } from '@markdawn/core';
import { Panel, Group, Separator } from 'react-resizable-panels';
import Toolbar from './components/Toolbar';
import Editor from './components/Editor';
import Preview from './components/Preview';
import PresentationView from './components/PresentationView';
import { DEFAULT_MARKDOWN } from './constants';
import { debounce } from './utils';

type ViewMode = 'split' | 'editor' | 'preview' | 'zen' | 'presentation';

function App() {
  const [markdown, setMarkdown] = useState<string>(() => {
    // Load from localStorage or use default
    const saved = localStorage.getItem('markdown-content');
    return saved || DEFAULT_MARKDOWN;
  });

  const [renderResult, setRenderResult] = useState<RenderResult | null>(null);
  const [theme, setTheme] = useState<'github-light' | 'github-dark'>(() => {
    const saved = localStorage.getItem('theme');
    return (saved as 'github-light' | 'github-dark') || 'github-light';
  });
  const [isRendering, setIsRendering] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('view-mode');
    return (saved as ViewMode) || 'split';
  });
  const mermaidInitialized = useRef(false);

  // Inject CSS themes on mount
  useEffect(() => {
    const styleId = 'markdown-viewer-themes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = getAllThemes();
      document.head.appendChild(style);
    }
  }, []);

  // Render markdown with debouncing
  const renderMarkdownDebounced = useCallback(
    debounce(async (content: string, selectedTheme: 'github-light' | 'github-dark') => {
      setIsRendering(true);
      try {
        const result = await renderMarkdown(content, {
          theme: selectedTheme,
          enableMermaid: true,
          enableMath: true,
          sanitize: true,
        });
        setRenderResult(result);

        // Initialize Mermaid if diagrams are present
        if (result.hasMermaid) {
          const initMermaid = async () => {
            const mermaid = (await import('mermaid')).default;

            if (!mermaidInitialized.current) {
              mermaid.initialize({
                startOnLoad: false,
                theme: selectedTheme === 'github-dark' ? 'dark' : 'default',
              });
              mermaidInitialized.current = true;
            }

            // Run mermaid on next tick to ensure DOM is updated
            setTimeout(() => {
              mermaid.run({
                querySelector: '.mermaid',
              });
            }, 100);
          };

          initMermaid();
        }
      } catch (error) {
        console.error('Rendering error:', error);
      } finally {
        setIsRendering(false);
      }
    }, 300),
    []
  );

  // Render on markdown or theme change
  useEffect(() => {
    renderMarkdownDebounced(markdown, theme);
  }, [markdown, theme, renderMarkdownDebounced]);

  // Save markdown to localStorage
  useEffect(() => {
    localStorage.setItem('markdown-content', markdown);
  }, [markdown]);

  // Save theme to localStorage and update document class
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'github-dark');
  }, [theme]);

  // Save view mode to localStorage
  useEffect(() => {
    localStorage.setItem('view-mode', viewMode);
  }, [viewMode]);

  // Handle Esc key for zen mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && viewMode === 'zen') {
        setViewMode('split');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode]);

  // Re-run mermaid when returning to preview modes from presentation
  useEffect(() => {
    const showsPreview = viewMode === 'split' || viewMode === 'preview';
    if (showsPreview && renderResult?.hasMermaid) {
      const runMermaid = async () => {
        const mermaid = (await import('mermaid')).default;

        if (!mermaidInitialized.current) {
          mermaid.initialize({
            startOnLoad: false,
            theme: theme === 'github-dark' ? 'dark' : 'default',
          });
          mermaidInitialized.current = true;
        }

        // Run mermaid on next tick to ensure DOM is updated
        setTimeout(() => {
          mermaid.run({
            querySelector: '.mermaid',
          });
        }, 100);
      };

      runMermaid();
    }
  }, [viewMode, renderResult?.hasMermaid, theme]);

  const handleClear = () => {
    if (confirm('Are you sure you want to clear the editor?')) {
      setMarkdown('');
    }
  };

  const handleCopyHTML = () => {
    if (renderResult) {
      navigator.clipboard.writeText(renderResult.html);
      alert('HTML copied to clipboard!');
    }
  };

  const handleLoadSample = (sample: string) => {
    setMarkdown(sample);
  };

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'github-light' ? 'github-dark' : 'github-light'));
    mermaidInitialized.current = false; // Reset mermaid to reinitialize with new theme
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
  };

  // Show presentation mode if active
  if (viewMode === 'presentation') {
    return (
      <PresentationView
        markdown={markdown}
        onExit={() => setViewMode('split')}
        theme={theme}
      />
    );
  }

  // Determine what to show based on view mode
  const showEditor = viewMode === 'split' || viewMode === 'editor' || viewMode === 'zen';
  const showPreview = viewMode === 'split' || viewMode === 'preview';
  const showToolbar = viewMode !== 'zen';

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-900">
      {showToolbar && (
        <Toolbar
          theme={theme}
          onToggleTheme={toggleTheme}
          onClear={handleClear}
          onCopyHTML={handleCopyHTML}
          onLoadSample={handleLoadSample}
          isRendering={isRendering}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      )}

      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {viewMode === 'split' ? (
          <Group orientation="horizontal">
            <Panel defaultSize={50} minSize={20}>
              <Editor value={markdown} onChange={setMarkdown} />
            </Panel>
            <Separator className="w-1 bg-gray-200 dark:bg-gray-700 hover:bg-blue-500 dark:hover:bg-blue-500 transition-colors cursor-col-resize" />
            <Panel defaultSize={50} minSize={20}>
              <Preview html={renderResult?.html || ''} isLoading={isRendering} />
            </Panel>
          </Group>
        ) : (
          <>
            {showEditor && <Editor value={markdown} onChange={setMarkdown} />}
            {showPreview && <Preview html={renderResult?.html || ''} isLoading={isRendering} />}
          </>
        )}
      </div>

      {/* Zen mode exit hint */}
      {viewMode === 'zen' && (
        <div className="fixed bottom-4 right-4 bg-black/70 text-white text-xs px-3 py-2 rounded-md">
          Press Esc or click here to exit Zen mode
          <button
            onClick={() => setViewMode('split')}
            className="ml-2 underline"
          >
            Exit
          </button>
        </div>
      )}
    </div>
  );
}

export default App;
